/*
 * Copyright (c) 2015 2017, Massachusetts Institute of Technology (MIT)
 * All rights reserved.
 * ====================================================================
 * This material may be reproduced by or for the
 * U.S. Government pursuant to the copyright license
 * under the clause at DFARS 252.227 7013 (June, 1995).
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions
 * are met:
 *
 * 1. Redistributions of source code must retain the above copyright
 * notice, this list of conditions and the following disclaimer.
 *
 * 2. Redistributions in binary form must reproduce the above copyright
 * notice, this list of conditions and the following disclaimer in the
 * documentation and/or other materials provided with the distribution.
 *
 * 3. Neither the name of the copyright holder nor the names of its
 * contributors may be used to endorse or promote products derived
 * from this software without specific prior written permission.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR
 * SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION)
 * HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT,
 * STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 * ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * ====================================================================
 */
define((require) => {
  'use strict';

  const ConversionModule = function() {};

  // Constants being used to help with conversions
  const INCHESTOMETERS = 0.0254;
  const FEETTOMETERS = INCHESTOMETERS * 12;
  const YARDSTOMETERS = FEETTOMETERS * 3;
  const MILESTOMETERS = 1609.344;
  const NAUTICALMILESTOMETERS = 1852;
  const MILLIMETERSTOMETERS = 0.001;
  const CENTIMETERSTOMETERS = 0.01;
  const KILOMETERSTOMETERS = 1000;

  const METERSTOINCHES = 1.0 / INCHESTOMETERS;
  const METERSTOFEET = 1.0 / FEETTOMETERS;
  const METERSTOYARDS = 1.0 / YARDSTOMETERS;
  const METERSTOMILES = 1.0 / MILESTOMETERS;
  const METERSTONAUTICALMILES = 1.0 / NAUTICALMILESTOMETERS;
  const METERSTOMILLIMETERS = 1000;
  const METERSTOCENTIMETERS = 100;
  const METERSTOKILOMETERS = 0.001;

  const KELVINCONVERTER = 273.15;
  const FOOTPERSECONDTOMETERSPERHOUR = 1097.28;
  const MPHTOMETERSPERHOUR = 1609.34;
  const METERSPERHOURTOFOOTPERSECOND = 0.000911344;
  const METERSPERHOURTOMPH = 0.0006213709090909091;

  const IN = 'in';
  const INCHES = 'inches';
  const FT = 'ft';
  const FEET = 'feet';
  const YD = 'yd';
  const YARD = 'yards';
  const MI = 'mi';
  const MILES = 'miles';
  const NM = 'nm';
  const NMI = 'nmi';
  const MM = 'mm';
  const MILLIMETERS = 'millimeters';
  const CM = 'cm';
  const CENTIMETERS = 'centimeters';
  const M = 'm';
  const METERS = 'meters';
  const KM = 'km';
  const KILOMETERS = 'kilometers';
    
  const CMH = 'cm/h';
  const MH = 'm/h';
  const KMH = 'km/h';
  const FTS = 'ft/s';
  const MPH = 'mph';
  const KNOT = 'knot';
  const KNOTS = 'knots';

  const KELVIN = 'k';
  const CELCIUS = 'c';
  const FAHRENHEIT = 'f';

  /**
   * Converts the value of a length from one unit to another.
   *
   * @param {(number)} value - The value that is being converted
   * @param {(string)} from - String that tells the function what the current unit is for the given value
   * @param {(string)} to - String that tells the function what the unit of the value should be when returning
   */
  ConversionModule.prototype.convertLength = function(value, from, to) {
    if (value != null && from && to) {
      if (value === 0 || from === to) {
        return value;
      }

      let newValue;

      // Convert from what we have to meters
      switch (from.toLowerCase()) {
      case IN:
      case INCHES:
        newValue = value * INCHESTOMETERS;
        break;
      case FT:
      case FEET:
        newValue = value * FEETTOMETERS;
        break;
      case YD:
      case YARD:
        newValue = value * YARDSTOMETERS;
        break;
      case MI:
      case MILES:
        newValue = value * MILESTOMETERS;
        break;
      case NM:
      case NMI:
        newValue = value * NAUTICALMILESTOMETERS;
        break;
      case MM:
      case MILLIMETERS:
        newValue = value * MILLIMETERSTOMETERS;
        break;
      case CM:
      case CENTIMETERS:
        newValue = value * CENTIMETERSTOMETERS;
        break;
      case M:
      case METERS:
        newValue = value;
        break;
      case KM:
      case KILOMETERS:
        newValue = value * KILOMETERSTOMETERS;
        break;
      default:
        return null;
      }

      // Convert from meters to what we want
      switch (to.toLowerCase()) {
      case IN:
      case INCHES:
        newValue = newValue * METERSTOINCHES;
        break;
      case FT:
      case FEET:
        newValue = newValue * METERSTOFEET;
        break;
      case YD:
      case YARD:
        newValue = newValue * METERSTOYARDS;
        break;
      case MI:
      case MILES:
        newValue = newValue * METERSTOMILES;
        break;
      case NM:
      case NMI:
        newValue = newValue * METERSTONAUTICALMILES;
        break;
      case MM:
      case MILLIMETERS:
        newValue = newValue * METERSTOMILLIMETERS;
        break;
      case CM:
      case CENTIMETERS:
        newValue = newValue * METERSTOCENTIMETERS;
        break;
      case M:
      case METERS:
        break;
      case KM:
      case KILOMETERS:
        newValue = newValue * METERSTOKILOMETERS;
        break;
      default:
        return null;
      }

      return newValue;            
    }

    return null;
  };

  /**
   * Converts the value of a velocity from one unit to another.
   *
   * @param {(number)} value - The value that is being converted
   * @param {(string)} from - String that tells the function what the current unit is for the given value
   * @param {(string)} to - String that tells the function what the unit of the value should be when returning
   */
  ConversionModule.prototype.convertVelocity = function(value, from, to) {
    if (value != null && from && to) {
      let newValue;

      switch (from.toLowerCase()) {
      case CMH:
        newValue = value * CENTIMETERSTOMETERS;
        break;
      case MH:
        newValue = value; 
        break;
      case KMH:
        newValue = value * KILOMETERSTOMETERS;
        break;
      case FTS:
        newValue = value * FOOTPERSECONDTOMETERSPERHOUR;
        break;
      case MPH:
        newValue = value * MPHTOMETERSPERHOUR;
        break;
      case KNOT:
      case KNOTS:
        newValue = value * NAUTICALMILESTOMETERS;
        break;
      default:
        return null;
      }

      switch (to.toLowerCase()) {
      case CMH:
        newValue = newValue * METERSTOCENTIMETERS;
        break;
      case MH:
        break;
      case KMH:
        newValue = newValue * METERSTOKILOMETERS;
        break;
      case FTS:
        newValue = newValue * METERSPERHOURTOFOOTPERSECOND;
        break;
      case MPH:
        newValue = newValue * METERSPERHOURTOMPH;
        break;
      case KNOT:
      case KNOTS:
        newValue = newValue * METERSTONAUTICALMILES;
        break;
      default:
        return null;
      }
      return newValue;
    }

    return null;
  };

  /**
   * Converts the value of a temperature from one unit to another.
   *
   * @param {(number)} value - The value that is being converted
   * @param {(string)} from - String that tells the function what the current unit is for the given value
   * @param {(string)} to - String that tells the function what the unit of the value should be when returning
   */
  ConversionModule.prototype.convertTemperature = function(value, from, to) {
    if (value != null && from && to) {
      const lowerCaseFrom = from.toLowerCase();
      const lowerCaseTo = to.toLowerCase();

      if (lowerCaseFrom === lowerCaseTo) {
        return value;
      }

      if (lowerCaseFrom === FAHRENHEIT && lowerCaseTo === CELCIUS) {
        return ((value - 32) * (5 / 9));
      } else if (lowerCaseFrom === FAHRENHEIT && lowerCaseTo === KELVIN) {
        return ((value - 32) * (5 / 9) + KELVINCONVERTER);
      } else if (lowerCaseFrom === CELCIUS && lowerCaseTo === FAHRENHEIT) {
        return ((value * (9 / 5)) + 32);
      } else if (lowerCaseFrom === CELCIUS && lowerCaseTo === KELVIN) {
        return value + KELVINCONVERTER;
      } else if (lowerCaseFrom === KELVIN && lowerCaseTo === FAHRENHEIT) {
        return ((value - KELVINCONVERTER) * (9 / 5) + 32);
      } else if (lowerCaseFrom === KELVIN && lowerCaseTo === CELCIUS) {
        return value - KELVINCONVERTER;
      } else {
        return null;
      }
    }

    return null;
  };

    /**
   * Converts date to another time zone, by offset.
   *
   * @param {(date)} date - The date that is being converted
   * @param {(int)} from - timezone offset from utc
   * @param {(to)} to - timezone offset from utc
   */
  ConversionModule.prototype.convertDate = function(date, to, from) {
    const offset = from || -1*(date.getTimezoneOffset()/60) ;
    const newDate = new Date(date.getTime()); 
    newDate.setHours(newDate.getHours() - offset + to); 
    return newDate;
  };

  return new ConversionModule();
});
